import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Trash2, Send, Eye, Loader2, Download } from 'lucide-react';

export default function EventRegistrationsList({ api, eventId, isAdmin }) {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');
  const [resendingId, setResendingId] = useState('');

  const fetchRegs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/events/${eventId}/registrations`);
      setRegs(res.data.registrations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  }, [api, eventId]);

  useEffect(() => { fetchRegs(); }, [fetchRegs]);

  const handleDelete = async (regId) => {
    if (!window.confirm('Delete this registration?')) return;
    try {
      await api.delete(`/events/${eventId}/registrations/${regId}`);
      toast.success('Registration deleted');
      fetchRegs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleResend = async (regId) => {
    setResendingId(regId);
    try {
      await api.post(`/events/${eventId}/registrations/${regId}/resend`);
      toast.success('Resend triggered. Status will update in a moment.');
      setTimeout(fetchRegs, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingId('');
    }
  };

  const cardUrl = (regId) => `${process.env.REACT_APP_BACKEND_URL}/api/public/event-card/${regId}.png`;

  const exportCsv = () => {
    if (!regs.length) return;
    const header = ['Name', 'ITS', 'Mobile', 'WhatsApp Status', 'Registered At'];
    const rows = regs.map(r => [
      (r.name || '').replace(/"/g, '""'),
      r.its || '',
      r.mobile_input || r.mobile || '',
      r.whatsapp_status || '',
      r.created_at || '',
    ]);
    const csv = [header, ...rows].map(row => row.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-${eventId}-registrations.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Public Registrations <span className="text-sm font-normal text-slate-500">({regs.length})</span></h3>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!regs.length} data-testid="event-regs-export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchRegs} data-testid="event-regs-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : regs.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-12 text-center text-slate-500">
          No registrations yet. Share the public registration link to start receiving entries.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>ITS</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Registered</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regs.map(r => (
                <TableRow key={r.id} data-testid={`event-reg-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>{r.its || '-'}</TableCell>
                  <TableCell>{r.mobile_input || r.mobile}</TableCell>
                  <TableCell>
                    <Badge variant={r.whatsapp_status === 'sent' ? 'default' : r.whatsapp_status === 'failed' ? 'destructive' : 'secondary'}>
                      {r.whatsapp_status || 'pending'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewUrl(cardUrl(r.id))} data-testid={`event-reg-preview-${r.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResend(r.id)}
                            disabled={resendingId === r.id}
                            data-testid={`event-reg-resend-${r.id}`}
                            title="Resend WhatsApp"
                          >
                            {resendingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(r.id)}
                            className="text-red-500"
                            data-testid={`event-reg-delete-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPreviewUrl('')}>
          <div className="bg-white rounded-xl p-3 max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={previewUrl} alt="ID Card" className="max-w-full max-h-[80vh] object-contain mx-auto" />
            <div className="mt-3 flex justify-end gap-2">
              <a href={previewUrl} download>
                <Button size="sm" variant="outline"><Download className="w-4 h-4 mr-1" /> Download</Button>
              </a>
              <Button size="sm" onClick={() => setPreviewUrl('')}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
